import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";

const AUTOPLAY_MS = 5500;

// Full-bleed rotating header of photos/videos with overlaid editorial title.
export default function HeaderCarousel({ overline, titleLines, intro, media = [], testid }) {
  const [index, setIndex] = useState(0);
  const count = media.length;

  const go = useCallback(
    (dir) => setIndex((i) => (i + dir + count) % count),
    [count]
  );
  const goTo = (i) => setIndex(i);

  useEffect(() => {
    if (count <= 1) return;
    const t = setTimeout(() => go(1), AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [index, count, go]);

  const active = media[index] || {};

  return (
    <header
      data-testid={testid}
      className="relative min-h-[78vh] w-full overflow-hidden bg-michma-ink"
    >
      {/* Media layers */}
      <AnimatePresence mode="sync">
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1 }, scale: { duration: 6, ease: "linear" } }}
          className="absolute inset-0"
        >
          {active.type === "video" ? (
            <video
              key={active.src}
              className="w-full h-full object-cover"
              src={active.src}
              poster={active.poster}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
            />
          ) : (
            <img
              src={active.src}
              alt=""
              className="w-full h-full object-cover"
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Contrast overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-michma-ink/85 via-michma-ink/45 to-michma-ink/40" />

      {/* Text — container defines the header height and grows with content */}
      <div className="relative min-h-[78vh] max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pt-32 md:pt-40 pb-24 md:pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="font-mono text-xs uppercase tracking-[0.25em] text-white/80 mb-5"
        >
          {overline}
        </motion.p>
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.95] text-white">
          <MaskedLines lines={titleLines} delay={0.2} />
        </h1>
        {intro && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="mt-6 max-w-2xl text-base sm:text-lg lg:text-xl leading-relaxed text-white/75"
          >
            {intro}
          </motion.p>
        )}
      </div>

      {/* Controls */}
      {count > 1 && (
        <>
          <div className="absolute bottom-8 right-6 md:right-12 flex items-center gap-3 z-10">
            <button
              onClick={() => go(-1)}
              data-testid="header-carousel-prev"
              aria-label="Previous slide"
              className="grid place-items-center w-11 h-11 rounded-full border border-white/40 text-white hover:bg-white hover:text-michma-ink transition-colors duration-300"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => go(1)}
              data-testid="header-carousel-next"
              aria-label="Next slide"
              className="grid place-items-center w-11 h-11 rounded-full border border-white/40 text-white hover:bg-white hover:text-michma-ink transition-colors duration-300"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="absolute bottom-8 left-6 md:left-12 flex items-center gap-2 z-10">
            {media.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                data-testid={`header-carousel-dot-${i}`}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === index ? "w-10 bg-white" : "w-4 bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </header>
  );
}
